import {
  SkyButton,
  SkyCard,
  SkyMessageBar,
  SkySkeleton,
  SkySkeletonItem,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import type { ReactNode } from "react";
import { CopyIcon, RetryIcon, type IconComponent } from "@/lib/icons";
import { referenceIdOf, referenceIdToShow } from "@/lib/api";

/**
 * The four states every platform screen shows (canvases 10 to 14, panel 03).
 *
 * They live in one module because the four are a set: a screen that has a
 * loading state and an empty state but no denied state looks finished and is
 * not, and the only reliable way to keep them together is to make them one
 * import. The copy differs per screen — it is passed in — but the SHAPE does
 * not, so a reader who has seen one platform screen has seen all five.
 */

/**
 * Skeleton rows that keep the grid's shape while it loads.
 *
 * Deliberately not a spinner: a grid that collapses to a centred spinner and
 * then expands shifts everything below it, and on a fast connection the shift
 * is the only thing the eye catches.
 */
export function GridSkeleton({
  rows = 4,
  label = "Loading…",
  testId,
}: {
  rows?: number;
  label?: string;
  testId?: string;
}) {
  return (
    <div data-testid={testId} role="status" aria-live="polite" aria-busy="true">
      <SkyText size={200} color="muted" block className="mb-2">
        {label}
      </SkyText>
      <SkySkeleton>
        <SkySkeletonItem shape="rectangle" size={44} count={rows} gap={8} />
      </SkySkeleton>
    </div>
  );
}

/**
 * The empty state. It names the consequence and offers the one action that
 * ends it, because "No tenants yet" alone tells a reader nothing they could
 * not see for themselves.
 */
export function EmptyState({
  title,
  message,
  icon: Icon,
  action,
  testId,
}: {
  title: string;
  message: string;
  icon?: IconComponent;
  action?: ReactNode;
  testId?: string;
}) {
  return (
    <SkyCard appearance="outline" testId={testId}>
      <div className="flex flex-col items-start gap-3 p-2">
        {Icon ? <Icon className="h-6 w-6 text-muted-foreground" /> : null}
        <SkyText as="h2" size={400} weight="semibold" block>
          {title}
        </SkyText>
        <SkyText as="p" size={300} color="muted" block className="max-w-prose">
          {message}
        </SkyText>
        {action}
      </div>
    </SkyCard>
  );
}

/**
 * An inline failure with the reference id and a retry.
 *
 * The id is shown rather than only logged: the toast that also carries it has
 * gone by the time anyone opens a support ticket, and a reader who can read the
 * id off the screen does not have to reproduce the failure to report it.
 * Copying is best-effort — an insecure origin or a denied permission leaves the
 * id on screen to be typed, which is why the id is never ONLY behind the button.
 */
export function ErrorState({
  title,
  error,
  onRetry,
  testId,
}: {
  title: string;
  error: unknown;
  onRetry?: () => void;
  testId?: string;
}) {
  const reference = referenceIdOf(error);
  const detail = error instanceof Error ? error.message : "Something went wrong.";
  // Printed only when the sentence does not already name it — the non-envelope
  // fallback in `lib/api.ts` writes the id into its own sentence. The COPY
  // button below still offers it either way: a reader who can see the id in the
  // prose should not have to select it by hand.
  const printed = referenceIdToShow(detail, reference);

  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      title={title}
      testId={testId}
      message={
        <span>
          {detail}
          {printed ? (
            <>
              {" "}
              Reference id <span className="font-mono">{printed}</span>.
            </>
          ) : null}
        </span>
      }
      actions={
        <div className="flex items-center gap-2">
          {reference ? (
            <SkyButton
              appearance="subtle"
              size="small"
              icon={CopyIcon}
              onClick={() => {
                void navigator.clipboard?.writeText(reference).catch(() => {
                  /* insecure origin or denied — the id is on screen to be typed */
                });
              }}
            >
              Copy reference
            </SkyButton>
          ) : null}
          {onRetry ? (
            <SkyButton appearance="secondary" size="small" icon={RetryIcon} onClick={onRetry}>
              Retry
            </SkyButton>
          ) : null}
        </div>
      }
    />
  );
}

/**
 * The note that explains what a screen is for, above its grid.
 *
 * Every platform canvas opens with one, and every one of them says something a
 * reader cannot infer from the column headers — what a tenant IS, that a
 * vault name is a pointer, that the egress list is computed.
 */
export function ScreenNote({
  title,
  message,
  testId,
}: {
  title: string;
  message: string;
  testId?: string;
}) {
  return (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      title={title}
      message={message}
      testId={testId}
    />
  );
}
